/**
 * Phase 4 — persist SOAP as Visit Note / NoteVersion.content.
 * Does not write diagnoses, reasonCode, or call sign.
 * localStorage remains a cache and is never deleted here.
 */
import { assertEncounterWritable } from "@/lib/encounterApi";
import { api } from "@/lib/orgApi";
import { hasSoapContent, isSoapServerPersistEnabled } from "@/lib/soapStorage";

const inFlight = new Map();
let visitTypeCache = null;

export function resetVisitNoteSoapCache() {
  visitTypeCache = null;
  inFlight.clear();
}

export function runExclusiveVisitNote(encounterId, fn) {
  const prior = inFlight.get(encounterId);
  const run = (prior || Promise.resolve()).then(fn);
  const guarded = run.catch((error) => {
    if (inFlight.get(encounterId) === guarded) inFlight.delete(encounterId);
    throw error;
  });
  inFlight.set(encounterId, guarded);
  return guarded.finally(() => {
    if (inFlight.get(encounterId) === guarded) inFlight.delete(encounterId);
  });
}

export async function getVisitNoteType() {
  if (visitTypeCache) return visitTypeCache;
  const { data } = await api.get("/note-types");
  const item = (data.items || []).find((row) => row.code === "visit");
  if (!item) throw new Error("Visit Note type is not available");
  visitTypeCache = item;
  return item;
}

export async function listVisitNotes(encounterId) {
  const { data } = await api.get("/notes", {
    params: { encounter: encounterId, noteType: "visit" },
  });
  return data.items || [];
}

export async function loadVisitSoapContent(encounterId, noteId = null) {
  if (!encounterId || !isSoapServerPersistEnabled()) return null;
  try {
    if (noteId) {
      const { data } = await api.get(`/notes/${noteId}`);
      const version = data.currentVersion;
      const content = version?.content || {};
      return {
        noteId: data.id,
        versionId: version?.id || null,
        versionStatus: version?.status || null,
        text: typeof content.text === "string" ? content.text : "",
        resolvedText: typeof content.resolvedText === "string" ? content.resolvedText : "",
      };
    }
    const items = await listVisitNotes(encounterId);
    if (!items.length) return null;
    const { data } = await api.get(`/notes/${items[0].id}`);
    const version = data.currentVersion;
    const content = version?.content || {};
    return {
      noteId: data.id,
      versionId: version?.id || null,
      versionStatus: version?.status || null,
      text: typeof content.text === "string" ? content.text : "",
      resolvedText: typeof content.resolvedText === "string" ? content.resolvedText : "",
    };
  } catch {
    return null;
  }
}

function soapContent(text, resolvedText) {
  return {
    format: "soap",
    text: text || "",
    ...(resolvedText ? { resolvedText } : {}),
  };
}

async function updateDraft(noteId, versionId, text, resolvedText) {
  const { data } = await api.put(`/notes/${noteId}/versions/${versionId}`, {
    content: soapContent(text, resolvedText),
  });
  return { noteId, versionId: data.id || versionId, updated: true };
}

async function persistToExistingNote(noteId, text, resolvedText) {
  const { data } = await api.get(`/notes/${noteId}`);
  const version = data.currentVersion;
  if (version?.status === "draft" && version.id) {
    await assertEncounterWritable(data.encounterId, { versionKind: version.kind });
    return updateDraft(data.id, version.id, text, resolvedText);
  }
  if (!version) {
    await assertEncounterWritable(data.encounterId);
    const draft = await api.post(`/notes/${data.id}/versions`, {
      content: soapContent(text, resolvedText),
    });
    return { noteId: data.id, versionId: draft.data.id, created: true };
  }
  return { noteId: data.id, versionId: version?.id || null, skipped: true, reason: "signed-or-locked" };
}

async function createVisitDraft(encounterId, text, resolvedText, noteId = null, allowCreateNew = false) {
  if (noteId) return persistToExistingNote(noteId, text, resolvedText);
  const visitType = await getVisitNoteType();
  const existing = await listVisitNotes(encounterId);
  if (existing.length && !allowCreateNew) {
    return persistToExistingNote(existing[0].id, text, resolvedText);
  }
  await assertEncounterWritable(encounterId);
  const created = await api.post("/notes", {
    encounterId,
    noteTypeId: visitType.id,
  });
  const createdId = created.data.id;
  const draft = await api.post(`/notes/${createdId}/versions`, {
    content: soapContent(text, resolvedText),
  });
  return { noteId: createdId, versionId: draft.data.id, created: true };
}

/** Prefer the PostgreSQL Visit Note. Use localStorage only when the server has no SOAP text. */
export function chooseSoapEditorText(localText, serverText) {
  if (hasSoapContent(serverText)) return serverText || "";
  if (hasSoapContent(localText)) return localText || "";
  return localText || serverText || "";
}

/**
 * If PostgreSQL has SOAP text, use it. If the server is empty and this browser
 * still has a localStorage draft, persist that draft to Notes once.
 */
export async function hydrateVisitSoap(encounterId, noteId = null, localDraft = null) {
  const server = await loadVisitSoapContent(encounterId, noteId);
  if (hasSoapContent(server?.text)) return server;
  const localText = localDraft?.text || "";
  if (!hasSoapContent(localText)) return server;
  const result = await persistVisitSoap({
    encounterId,
    text: localText,
    resolvedText: localDraft?.resolvedText || "",
    documented: true,
    explicitCreate: true,
    noteId,
  });
  return {
    noteId: result?.noteId || server?.noteId || noteId || null,
    versionId: result?.versionId || server?.versionId || null,
    text: localText,
    resolvedText: localDraft?.resolvedText || "",
  };
}

/**
 * Persist SOAP to a Visit Note draft.
 * Opening / empty / undocumented sessions skip server writes.
 */
export async function persistVisitSoap({
  encounterId,
  text,
  resolvedText,
  documented = false,
  explicitCreate = false,
  noteId = null,
  allowCreateNew = false,
}) {
  if (!encounterId || !isSoapServerPersistEnabled()) {
    return { skipped: true, reason: "disabled-or-missing-encounter" };
  }
  const shouldWrite = explicitCreate || (documented && hasSoapContent(text));
  if (!shouldWrite) return { skipped: true, reason: "no-documentation-act" };
  // Each branch below asserts writability once it knows whether it is updating an
  // open draft (an addendum survives Close Encounter) or creating net-new content.
  return runExclusiveVisitNote(encounterId, () =>
    createVisitDraft(encounterId, text, resolvedText, noteId, allowCreateNew),
  );
}
